import React from 'react';
import { CheckboxProps } from '../../types';

const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  onChange,
  label,
  error,
  disabled = false,
  required = false,
  className = '',
  id,
}) => {
  const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
  
  const baseClasses = 'w-4 h-4 border rounded transition-all duration-normal focus:outline-none focus:ring-2 focus:ring-offset-0';
  
  const stateClasses = error
    ? 'border-error-300 focus:border-error-500 focus:ring-error-500'
    : 'border-neutral-300 focus:border-primary-500 focus:ring-primary-500';
  
  const checkedClasses = checked
    ? 'bg-primary-500 border-primary-500 text-white'
    : 'bg-white';
  
  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed'
    : 'cursor-pointer';
  
  const checkboxClasses = `
    ${baseClasses}
    ${stateClasses}
    ${checkedClasses}
    ${disabledClasses}
    ${className}
  `.trim();
  
  return (
    <div className="w-full">
      <div className="flex items-start gap-3">
        <div className="relative flex items-center">
          <input
            id={checkboxId}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange?.(e.target.checked)}
            disabled={disabled}
            required={required}
            className="sr-only"
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error ? `${checkboxId}-error` : undefined
            }
          />
          <label
            htmlFor={checkboxId}
            className={checkboxClasses}
          >
            {checked && (
              <svg
                className="w-3 h-3"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </label>
        </div>
        
        {label && (
          <label
            htmlFor={checkboxId}
            className="text-sm font-medium text-neutral-700 cursor-pointer select-none"
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}
      </div>
      
      {error && (
        <p
          id={`${checkboxId}-error`}
          className="text-error-600 text-sm mt-1 ml-7"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default Checkbox;
